/**
 * HTTP API client
 */
import { config } from './config.js';
import { TEXT } from './ui-text.js';
import {
  clearAll as clearMessageCache,
  sealWrites as sealMessageCache,
  unsealWrites as unsealMessageCache,
} from './message-cache.js';

class ApiError extends Error {
  constructor(status, message, options = {}) {
    super(message);
    this.status = status;
    this.code = options.code || null;
    this.path = options.path || null;
    // Структурированный detail: тексту в UI он не нужен, а вызывающему коду
    // бывает нужен, чтобы отличить один отказ от другого.
    this.detail = options.detail ?? null;
  }
}

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function describeTimeout(timeoutMs) {
  const seconds = Math.round(timeoutMs / 1000);
  console.warn(`API timeout after ${seconds}s`);
  return TEXT.api.timeout;
}

let loginRedirectInFlight = false;

// Задержка между ретраями с джиттером: iOS WebKit «прерывисто» роняет fetch — мгновенный
// повтор попадает в то же окно сбоя; 0.5-1.5с с разбросом ±30% разводит попытки.
// (Локальный хелпер: stores.js импортирует api.js — оттуда delay не взять, цикл.)
const RETRY_DELAYS_MS = [500, 1500];
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
// Этап 33.2: включение модели ждёт пробного запроса к ней (backend укладывается в
// 100 с, nginx ждёт /api 120 с).
const MODEL_CHECK_TIMEOUT_MS = 115000;

function retryDelay(attempt) {
  const base = RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)];
  const jitter = base * 0.3 * (Math.random() * 2 - 1);
  return Math.max(100, Math.round(base + jitter));
}

function sleep(ms) {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

// При 401 (нет валидной session-cookie) — событие «нужен вход»: App.svelte показывает
// экран входа (этап 16: Face ID или ссылка на сертификатный хост). Раньше здесь был
// немедленный уход навигацией на mTLS login-порт; он остался внутри экрана входа как
// запасной путь. Защита от шторма: не чаще раза в 20с.
export const LOGIN_REQUIRED_EVENT = 'app:login-required';

function maybeRedirectToLogin() {
  if (typeof window === 'undefined' || loginRedirectInFlight) {
    return;
  }
  const key = 'petrovich_login_redirect_at';
  let last = 0;
  try { last = Number(window.sessionStorage.getItem(key) || '0'); } catch { /* private mode */ }
  if (Date.now() - last < 20000) {
    return; // только что вошли и снова 401 — не зацикливаемся
  }
  loginRedirectInFlight = true;
  // Пере-логин может произойти под ДРУГИМ пользователем — не оставляем чужие сообщения
  // в кэше на диске (приватность). Снимки перестроятся после входа. Печать снимает
  // первый успешный ответ API: без неё onDestroy экрана чата, который отработает
  // уже после очистки, вернул бы переписку на диск.
  clearMessageCache();
  sealMessageCache();
  try { window.sessionStorage.setItem(key, String(Date.now())); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(LOGIN_REQUIRED_EVENT));
}

// Уход НАВИГАЦИЕЙ на mTLS login-хост за сертом — iOS WebKit отдаёт серт для навигации,
// но не для fetch. Backend по серту ставит cookie и возвращает в приложение.
export function goToCertificateLogin() {
  if (typeof window === 'undefined' || !config.loginUrl) {
    return;
  }
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${config.loginUrl}?return_to=${returnTo}`;
}

// Экран входа закрыт успешным входом — следующий 401 снова покажет его.
export function resetLoginRedirect() {
  loginRedirectInFlight = false;
}

// Условные GET (1.24.2): backend отдаёт ETag на список чатов и страницу истории
// (этап 20), но Safari в PWA условных запросов сам не шлёт — за день в проде ни
// одного 304. Поэтому теги и тела помним здесь: If-None-Match уходит от нас, на 304
// отдаём тело из памяти. Страница чата с фото весит мегабайты, а перечитывается
// на каждом resume и реконнекте — это главный сберегаемый трафик. Этап 30: сюда же
// первый экран (/start) и открытие чата (/open) — они тоже отвечают с ETag.
const ETAG_CACHE_LIMIT = 12;
const etagCache = new Map();

function etagCacheKey(method, path) {
  if (method !== 'GET') {
    return null;
  }
  return /^\/(start|chats)(\?|$)|^\/chats\/[^/]+\/(messages|open)(\?|$)/.test(path) ? path : null;
}

function rememberEtag(key, etag, body) {
  if (!key || !etag) {
    return;
  }
  etagCache.delete(key);
  etagCache.set(key, { etag, body });
  while (etagCache.size > ETAG_CACHE_LIMIT) {
    etagCache.delete(etagCache.keys().next().value);
  }
}

export function clearEtagCache() {
  etagCache.clear();
}

async function performFetch(method, path, body, timeoutMs, extraHeaders = null) {
  const options = {
    method,
    // Шлём session-cookie (same-origin). На resume cookie не зависит от mTLS
    // cert-handshake → fetch /api не падает «access control checks».
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(extraHeaders || {}),
    },
  };

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId = null;
  if (controller && timeoutMs > 0) {
    options.signal = controller.signal;
    timeoutId = globalThis.setTimeout(() => {
      controller.abort();
    }, timeoutMs);
  }

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    return await fetch(`${config.apiUrl}${path}`, options);
  } finally {
    if (timeoutId) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

async function request(method, path, body = null, { timeoutMs = config.apiTimeoutMs, noLoginRedirect = false } = {}) {
  const maxAttempts = Math.max(1, config.apiRetryAttempts || 1);
  let lastError = null;

  const cacheKey = etagCacheKey(method, path);
  const cached = cacheKey ? etagCache.get(cacheKey) : null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    let response;
    try {
      response = await performFetch(method, path, body, timeoutMs, cached ? { 'If-None-Match': cached.etag } : null);
    } catch (error) {
      lastError = error;
      // Зависший/оборванный fetch на iOS WebKit бывает прерывистым — повторяем с
      // короткой паузой, прежде чем сдаться. HTTP-ответ обрабатывается ниже.
      if (attempt < maxAttempts) {
        await sleep(retryDelay(attempt));
        continue;
      }
      if (isAbortError(error)) {
        throw new ApiError(0, describeTimeout(timeoutMs), { code: 'TIMEOUT', path });
      }
      throw new ApiError(0, TEXT.api.network, { code: 'NETWORK', path });
    }

    if (response.status === 304 && cached) {
      unsealMessageCache();
      return cached.body;
    }

    if (!response.ok) {
      // Транзиентные 502/503/504 (флап туннеля/бэка) ретраим — но только идемпотентные
      // GET: повтор мутирующего запроса на 5xx мог бы задублировать эффект.
      if (method === 'GET' && RETRYABLE_STATUSES.has(response.status) && attempt < maxAttempts) {
        lastError = new ApiError(response.status, TEXT.api.serverBusy, { path });
        await sleep(retryDelay(attempt));
        continue;
      }
      if (response.status === 401 && !noLoginRedirect) {
        // Нет валидной session-cookie → экран входа (этап 16), а он уже сам решает,
        // Face ID или сертификат. Тела чужой сессии в памяти не держим.
        clearEtagCache();
        maybeRedirectToLogin();
      }
      const text = await response.text();
      let message;
      let detail = null;
      try {
        const json = JSON.parse(text);
        detail = json.detail ?? null;
        // detail/message бэкенда — осмысленный текст; НЕ-JSON тело (HTML-страница
        // nginx при 502) в UI не показываем — только словарная строка, сырое в console.
        // Объектный detail в message не кладём: в интерфейс попал бы [object Object].
        const detailText = typeof detail === 'string' ? detail : null;
        message = detailText || json.message || TEXT.api.serverBusy;
      } catch {
        console.warn(`API ${response.status} on ${path}:`, text.slice(0, 300));
        message = response.status >= 500 ? TEXT.api.serverBusy : TEXT.api.network;
      }
      throw new ApiError(response.status, message, { path, detail });
    }

    // Ответ получен — значит сессия жива: снимки снова можно писать.
    unsealMessageCache();
    if (response.status === 204) {
      return null;
    }
    const json = await response.json();
    if (cacheKey) {
      rememberEtag(cacheKey, response.headers?.get?.('etag') || null, json);
    }
    return json;
  }

  throw lastError;
}

// Этап 31: картинки сообщений ссылками, а не data URL внутри JSON (страница путевого
// чата весила 4,4 МБ). Без параметра backend отдаёт прежний формат — для бандлов,
// установленных до обновления.
const IMAGES_QUERY = '&images=links';

function stageQuery({ stage = null, stages = null } = {}) {
  let query = '';
  if (stage) {
    query += `&stage=${encodeURIComponent(stage)}`;
  }
  if (stages) {
    query += `&stages=${encodeURIComponent(stages)}`;
  }
  return query;
}

export const api = {
  // User
  async whoami() {
    return request('GET', '/whoami');
  },

  // Этап 30: первый экран одним запросом — whoami, общие настройки, модели и список
  // чатов (iOS WebKit роняет параллельные fetch, и четыре запроса шли по очереди).
  async start() {
    return request('GET', '/start');
  },

  async listModels() {
    return request('GET', '/models');
  },

  async getAppSettings() {
    return request('GET', '/settings/app');
  },

  async updateAppSettings(settings) {
    return request('PUT', '/settings/app', settings);
  },
  
  // Chats
  async listChats(archivedOnly = false) {
    const params = archivedOnly ? '?archivedOnly=1' : '';
    return request('GET', `/chats${params}`);
  },

  async getActivity() {
    return request('GET', '/activity');
  },

  async searchHistory(query, { limit = 20, cursor = null, archived = 'all' } = {}) {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
      archived,
    });
    if (cursor) {
      params.set('cursor', cursor);
    }
    return request('GET', `/search?${params.toString()}`);
  },
  
  // Библиотека шаблонов чата (этап 24).
  async listTemplates() {
    return request('GET', '/templates');
  },

  async createTemplate(fields) {
    return request('POST', '/templates', fields);
  },

  async updateTemplate(templateId, fields) {
    return request('PATCH', `/templates/${encodeURIComponent(templateId)}`, fields);
  },

  async deleteTemplate(templateId) {
    await request('DELETE', `/templates/${encodeURIComponent(templateId)}`);
  },

  async createChat(title = null, chatType = null, templateId = null) {
    const body = {};
    if (title) body.title = title;
    if (chatType) body.chatType = chatType;
    if (templateId) body.templateId = templateId;
    return request('POST', '/chats', body);
  },
  
  async updateChatTitle(chatId, title) {
    return request('PATCH', `/chats/${chatId}`, { title });
  },
  
  async archiveChat(chatId) {
    return request('POST', `/chats/${chatId}/archive`);
  },
  
  async unarchiveChat(chatId) {
    return request('POST', `/chats/${chatId}/unarchive`);
  },
  
  async deleteChat(chatId) {
    return request('DELETE', `/chats/${chatId}`);
  },
  
  // Этап 16: устройства, приглашения, passkey.
  async listDevices() {
    return request('GET', '/devices');
  },

  async revokeDevice(deviceId) {
    return request('DELETE', `/devices/${encodeURIComponent(deviceId)}`);
  },

  // Удалить passkey устройства с сервера — отдельно от отзыва сессии.
  async deleteDevicePasskey(deviceId) {
    return request('DELETE', `/devices/${encodeURIComponent(deviceId)}/passkey`);
  },

  async createDeviceInvite(userId = null) {
    return request('POST', '/devices/invites', userId ? { userId } : {});
  },

  async getPasskeyRegisterOptions() {
    return request('POST', '/webauthn/register/options', {});
  },

  async verifyPasskeyRegistration(body) {
    return request('POST', '/webauthn/register/verify', body);
  },

  // Вход по ключу — публичные пути: 401 тут невозможен, но и редиректа на
  // сертификатный хост при ошибке быть не должно (см. request → maybeRedirectToLogin).
  // Этап 25.1: состояние «мозга» — всем уровень, владельцу сервера ещё и провайдеры.
  async getBrainStatus() {
    return request('GET', '/status/brain');
  },

  // Этап 33: каталог моделей провайдеров — только владельцу сервера.
  async getModelCatalog() {
    return request('GET', '/models/catalog');
  },

  async markModelCatalogSeen() {
    return request('POST', '/models/catalog/seen');
  },

  // Этап 33.2: включить (с пробным запросом к модели — до ~100 с), выключить или
  // сделать основной. Ответ — каталог целиком.
  async updateModelCatalog(change) {
    return request('PATCH', '/models/catalog', change, { timeoutMs: MODEL_CHECK_TIMEOUT_MS });
  },

  async getPasskeyLoginOptions() {
    return request('POST', '/webauthn/login/options', {}, { noLoginRedirect: true });
  },

  async verifyPasskeyLogin(body) {
    return request('POST', '/webauthn/login/verify', body, { noLoginRedirect: true });
  },

  // Messages
  // Этап 23: `stage` — окно одного этапа, `stages` — 'folded' (по умолчанию) | 'flat'.
  // Этап 31: история и открытие чата — с картинками-ссылками (IMAGES_QUERY).
  async getMessages(chatId, limit = 50, before = null, { stage = null, stages = null } = {}) {
    let path = `/chats/${chatId}/messages?limit=${limit}${IMAGES_QUERY}`;
    if (before) {
      path += `&before=${encodeURIComponent(before)}`;
    }
    return request('GET', path + stageQuery({ stage, stages }));
  },

  // Этап 13: окно от начала истории и догрузка вперёд (order=asc / after).
  async getMessagesFromStart(chatId, limit = 50, { stage = null, stages = null } = {}) {
    return request('GET', `/chats/${chatId}/messages?limit=${limit}${IMAGES_QUERY}&order=asc${stageQuery({ stage, stages })}`);
  },

  // Этап 30: открытие чата одним запросом — хвост истории, настройки, мета по типу,
  // этапы и публикация (без коммитов). messages=false — возврат в открытый чат.
  async openChat(chatId, { limit = 50, messages = true } = {}) {
    return request('GET', `/chats/${chatId}/open?limit=${limit}${IMAGES_QUERY}${messages ? '' : '&messages=0'}`);
  },

  async getMessagesAfter(chatId, after, limit = 50, { stage = null, stages = null } = {}) {
    return request(
      'GET',
      `/chats/${chatId}/messages?limit=${limit}${IMAGES_QUERY}&order=asc&after=${encodeURIComponent(after)}${stageQuery({ stage, stages })}`
    );
  },

  // Этап 15: удаление обмена целиком и всей истории выше сообщения.
  async deleteMessage(chatId, messageId) {
    return request('DELETE', `/chats/${chatId}/messages/${encodeURIComponent(messageId)}`);
  },

  async deleteMessagesBefore(chatId, messageId) {
    return request('DELETE', `/chats/${chatId}/messages?before=${encodeURIComponent(messageId)}`);
  },

  // 1.23.3: «удалить всё ниже» — всё позже конца обмена этого сообщения.
  async deleteMessagesAfter(chatId, messageId) {
    return request('DELETE', `/chats/${chatId}/messages?after=${encodeURIComponent(messageId)}`);
  },

  // Правка сообщения (этап 22): хвост либо удаляется, либо остаётся в ленте вне контекста.
  async deleteMessagesFrom(chatId, messageId) {
    return request('DELETE', `/chats/${chatId}/messages?from=${encodeURIComponent(messageId)}`);
  },

  async excludeMessagesFrom(chatId, messageId) {
    return request('POST', `/chats/${chatId}/messages/${encodeURIComponent(messageId)}/exclude`);
  },

  async getMessagesAround(chatId, messageId, limit = 50, { stage = null, stages = null } = {}) {
    const params = new URLSearchParams({
      messageId,
      limit: String(limit),
      images: 'links',
    });
    if (stage) {
      params.set('stage', stage);
    }
    if (stages) {
      params.set('stages', stages);
    }
    return request('GET', `/chats/${chatId}/messages/around?${params.toString()}`);
  },

  // Этапы проекта (этап 23).
  async getStages(chatId) {
    return request('GET', `/chats/${chatId}/stages`);
  },

  async closeStage(chatId, title = null) {
    return request('POST', `/chats/${chatId}/stages`, title ? { title } : {});
  },

  async updateStage(chatId, stageId, fields) {
    return request('PATCH', `/chats/${chatId}/stages/${encodeURIComponent(stageId)}`, fields);
  },

  async regenerateStage(chatId, stageId, fromScratch = false) {
    return request('POST', `/chats/${chatId}/stages/${encodeURIComponent(stageId)}/regenerate`, { fromScratch });
  },

  async reopenStage(chatId, stageId) {
    await request('DELETE', `/chats/${chatId}/stages/${encodeURIComponent(stageId)}`);
  },

  async getProjectState(chatId) {
    return request('GET', `/chats/${chatId}/project-state`);
  },

  async updateProjectState(chatId, summary) {
    return request('PATCH', `/chats/${chatId}/project-state`, { summary });
  },

  async regenerateProjectState(chatId, fromScratch = false) {
    return request('POST', `/chats/${chatId}/project-state/regenerate`, { fromScratch });
  },

  async clearProjectState(chatId) {
    await request('DELETE', `/chats/${chatId}/project-state`);
  },

  // Публикация чата в git и на сайт (этап 26).
  async getPublication(chatId) {
    return request('GET', `/chats/${chatId}/publication`);
  },

  async publishChat(chatId, { includeTranscript = true, autoStages = true } = {}) {
    return request('POST', `/chats/${chatId}/publication`, { includeTranscript, autoStages });
  },

  async updatePublication(chatId, fields) {
    return request('PATCH', `/chats/${chatId}/publication`, fields);
  },

  async unpublishChat(chatId) {
    await request('DELETE', `/chats/${chatId}/publication`);
  },

  async resolvePublication(chatId, path, keep) {
    return request('POST', `/chats/${chatId}/publication/resolve`, { path, keep });
  },

  // Статья о проекте из сводки и конспектов (этап 26.5).
  async getArticle(chatId) {
    return request('GET', `/chats/${chatId}/article`);
  },

  async generateArticle(chatId) {
    return request('POST', `/chats/${chatId}/article`, {});
  },

  async acceptArticle(chatId) {
    return request('POST', `/chats/${chatId}/article/accept`, {});
  },

  async discardArticleDraft(chatId) {
    return request('DELETE', `/chats/${chatId}/article/draft`);
  },
  
  // Settings
  async getChatSettings(chatId) {
    return request('GET', `/chats/${chatId}/settings`);
  },
  
  async updateChatSettings(chatId, settings) {
    return request('PUT', `/chats/${chatId}/settings`, settings);
  },

  async resetChatContext(chatId) {
    await request('POST', `/chats/${chatId}/reset`);
    return null;
  },

  async getTravelChat(chatId) {
    return request('GET', `/chats/${chatId}/travel`);
  },

  async updateTravelChat(chatId, profile) {
    return request('PUT', `/chats/${chatId}/travel`, { profile });
  },

  async updateTravelPlace(chatId, placeId, fields) {
    return request('PATCH', `/chats/${chatId}/travel/places/${placeId}`, fields);
  },

  async deleteTravelPlace(chatId, placeId) {
    await request('DELETE', `/chats/${chatId}/travel/places/${placeId}`);
    return null;
  },

  // Общий список чата (этап 8): каждая мутация возвращает СНИМОК списка.
  async getChatList(chatId) {
    return request('GET', `/chats/${chatId}/list`);
  },

  async addChatListItem(chatId, text) {
    return request('POST', `/chats/${chatId}/list/items`, { text });
  },

  async updateChatListItem(chatId, itemId, fields) {
    return request('PATCH', `/chats/${chatId}/list/items/${itemId}`, fields);
  },

  async deleteChatListItem(chatId, itemId) {
    return request('DELETE', `/chats/${chatId}/list/items/${itemId}`);
  },

  async clearDoneChatListItems(chatId) {
    return request('POST', `/chats/${chatId}/list/clear-done`);
  },

  async getScanChat(chatId) {
    return request('GET', `/chats/${chatId}/scan`);
  },

  // Web Push (этап 7.5)
  async getPushConfig() {
    return request('GET', '/push/config');
  },

  async savePushSubscription(subscription) {
    await request('POST', '/push/subscriptions', subscription);
    return null;
  },

  async deletePushSubscription(endpoint) {
    await request('DELETE', '/push/subscriptions', { endpoint });
    return null;
  },

  async sendTestPush() {
    return request('POST', '/push/test');
  },

  // Уведомления по чату (этап 12): выключены по умолчанию, включаются точечно.
  async setChatNotifications(chatId, enabled) {
    await request('POST', `/chats/${chatId}/notifications/${enabled ? 'enable' : 'disable'}`);
    return null;
  },

  // Закрепление чата сверху (этап 11): ответ несёт метку pinnedAt — по ней клиент
  // ставит чат в нужное место списка, не перезагружая ленту.
  async pinChat(chatId, pinned) {
    return request('POST', `/chats/${chatId}/${pinned ? 'pin' : 'unpin'}`);
  },

  // Дача как второй мозг (этап 10): статус демона из retained heartbeat и живая
  // сводка дома через tool-RPC (до 15 с на стороне бэкенда — таймаут с запасом).
  async getDachaStatus() {
    return request('GET', '/dacha/status');
  },

  async getDachaSummary() {
    return request('GET', '/dacha/summary', null, { timeoutMs: Math.max(config.apiTimeoutMs, 20000) });
  },

  // Персональные MQTT-креды (этап 7.4): по cookie-сессии, пароль в бандле не живёт.
  async getMqttCredentials() {
    return request('GET', '/mqtt/credentials');
  },

  // Семейные чаты (этап 7.1)
  async listUsers() {
    return request('GET', '/users');
  },

  async getChatMembers(chatId) {
    return request('GET', `/chats/${chatId}/members`);
  },

  async shareChat(chatId, userIds, fromNow = false) {
    return request('POST', `/chats/${chatId}/share`, { userIds, fromNow });
  },

  async markChatRead(chatId, ts) {
    return request('POST', `/chats/${chatId}/read`, { ts });
  },

  async leaveChat(chatId) {
    await request('POST', `/chats/${chatId}/leave`);
    return null;
  },

  async removeChatMember(chatId, userId) {
    await request('DELETE', `/chats/${chatId}/members/${encodeURIComponent(userId)}`);
    return null;
  },

  async updateScanFragment(chatId, fragmentId, fields) {
    return request('PATCH', `/chats/${chatId}/scan/fragments/${fragmentId}`, fields);
  },

  async deleteScanFragment(chatId, fragmentId) {
    await request('DELETE', `/chats/${chatId}/scan/fragments/${fragmentId}`);
    return null;
  },
};

export { ApiError };
